/**
 * Whether a request will be sent to the Akili API's origin.
 *
 * The shared axios instance in `axios.tsx` attaches the Keycloak bearer token to its requests.
 * axios uses an absolute `url` as it is and ignores `baseURL`, so a call such as
 * `axios.get("https://example.org/x")` made on that instance would carry the user's token to a
 * host outside the product (AKL-1110). The request interceptor asks this function first and
 * sends the token only when the answer is `true`.
 *
 * Only the origin (scheme, host and port) is compared: axios joins a relative `url` onto
 * `baseURL`, which can change the path but never the origin. A protocol-relative `//host/x`
 * counts as absolute, as it does in axios, and resolves to `host`.
 *
 * Anything that cannot be parsed is treated as **not** the API, so a malformed URL loses the
 * token rather than keeping it.
 * @param requestUrl the request's `url` (absolute, protocol-relative or relative to the base)
 * @param requestBaseUrl the `baseURL` the request will be resolved against
 * @param apiBaseUrl the configured API address (`REACT_APP_SERVER_URL`); when unset the API is
 *   taken to live on the page's own origin
 * @returns true when the request goes to the API's origin, false for any other origin
 */
export function isApiRequest(
  requestUrl: string | undefined,
  requestBaseUrl: string | undefined,
  apiBaseUrl: string | undefined,
): boolean {
  try {
    // `origin` rather than `href`: a test (or embedder) can replace `window.location` with an
    // object whose `href` is empty, and `new URL("", "")` throws.
    const page = `${window.location.origin}/`;
    const apiOrigin = new URL(apiBaseUrl || "", page).origin;
    const requestOrigin = new URL(
      requestUrl || "",
      new URL(requestBaseUrl || "", page),
    ).origin;
    // `"null"` is the origin of `data:`, `file:` and the like; two of them are not "the same".
    return requestOrigin !== "null" && requestOrigin === apiOrigin;
  } catch {
    return false;
  }
}
